import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { CheckCircle2, KeyRound, Loader2, Mail, PlugZap, Save, ScanFace, ShieldCheck, Trash2, XCircle } from "lucide-react";

const err = (e) => e?.response?.data?.detail || "Falha na operação";

const Configured = ({ ok, testid }) => (
  <Badge className={ok ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-600"} data-testid={testid}>{ok ? "Configurado" : "Não configurado"}</Badge>
);

const Line = ({ ok, text, testid }) => (
  <span className={`inline-flex items-start gap-1 text-xs ${ok ? "text-green-700" : "text-rose-600"}`} data-testid={testid}>
    {ok ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <XCircle className="h-4 w-4 shrink-0" />}{text}</span>
);

const TestResult = ({ res }) => (
  <div className="mt-2 flex flex-col gap-1">
    <Line ok={!res.storage ? res.ok : true} text={res.message} testid="cf-test-result" />
    {res.storage && <Line ok={res.storage.ok} text={res.storage.message} testid="cf-test-storage" />}
  </div>
);

const KIND = { NAO_VINCULADO: "Não reconhecido como ele mesmo", CONFLITO: "Rosto igual ao de outro aluno", ERRO: "Erro ao conferir" };

const VerifyResult = ({ v }) => {
  if (v.status === "RUNNING") return <p className="mt-3 text-sm text-slate-500" data-testid="cf-verify-running"><Loader2 className="mr-1 inline h-4 w-4 animate-spin" />Conferindo os rostos…</p>;
  if (v.status === "ERROR") return <p className="mt-3 text-sm text-rose-700" data-testid="cf-verify-error">{v.message}</p>;
  if (v.status !== "DONE") return null;
  return (
    <div className="mt-3 rounded-lg bg-slate-50 p-3 text-sm" data-testid="cf-verify-result">
      <p className="font-semibold" data-testid="cf-verify-summary">{v.ok} de {v.checked} aluno(s) vinculados corretamente{v.orphans_removed ? ` · ${v.orphans_removed} cadastro(s) sem aluno removido(s) do CompreFace` : ""}</p>
      {v.problems?.length > 0 && (
        <ul className="mt-2 space-y-1">
          {v.problems.map((p) => (
            <li key={p.student_id} className="rounded bg-white px-2 py-1.5 text-rose-700" data-testid={`cf-verify-problem-${p.student_id}`}>
              <b>{p.full_name}</b> — {KIND[p.kind]}{p.others?.length ? `: ${p.others.map((o) => `${o.full_name} (${o.similarity}%)`).join(", ")}` : ""}{p.detail ? `: ${p.detail}` : ""}
            </li>
          ))}
          <li className="text-xs text-slate-500">Abra o perfil dos alunos indicados (inclusive o que aparece entre parênteses) e confira as fotos. Remova a biometria do aluno com a foto errada e recadastre-o em Presença → Biometria. O quiosque não registra chegada quando dois alunos parecem iguais.</li>
        </ul>
      )}
    </div>
  );
};

const useVerify = (sid) => {
  const [v, setV] = useState(null);
  const load = useCallback(() => api.get(`/integrations/compreface/${sid}/verify`).then((r) => setV(r.data)).catch(() => {}), [sid]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (v?.status !== "RUNNING") return undefined;
    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, [v, load]);
  const start = async () => {
    try { const { data } = await api.post(`/integrations/compreface/${sid}/verify`); setV(data); } catch (e) { toast.error(err(e)); }
  };
  return [v, start];
};

const SchoolConfig = ({ s, onSaved }) => {
  const [verify, startVerify] = useVerify(s.id);
  const [url, setUrl] = useState(s.compreface_url || "");
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(null);
  const [res, setRes] = useState(null);
  const save = async () => {
    setBusy("save");
    try {
      await api.put(`/integrations/compreface/${s.id}`, { url, api_key: key || null });
      toast.success(`${s.name}: CompreFace salvo — recadastrando as fotos guardadas em segundo plano`);
      setKey(""); onSaved();
    } catch (e) { toast.error(err(e)); } finally { setBusy(null); }
  };
  const wipe = async () => {
    if (!window.confirm(`Apagar TODOS os rostos de ${s.name} no CompreFace e zerar a biometria dos alunos? Os alunos precisarão ser fotografados de novo.`)) return;
    setBusy("wipe");
    try {
      const { data } = await api.post(`/integrations/compreface/${s.id}/wipe`);
      toast.success(`${data.removed} rosto(s) apagado(s) do CompreFace · biometria zerada em ${data.students_cleared} aluno(s)`);
      onSaved();
    } catch (e) { toast.error(err(e)); } finally { setBusy(null); }
  };
  const test = async () => {
    setBusy("test"); setRes(null);
    try { const { data } = await api.post(`/integrations/compreface/${s.id}/test`); setRes(data); }
    catch (e) { setRes({ ok: false, message: err(e) }); } finally { setBusy(null); }
  };
  return (
    <div className="border-t py-4 first:border-t-0" data-testid={`cf-school-${s.id}`}>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <b className="text-sm">{s.name}</b><Configured ok={s.configured} testid={`cf-configured-${s.id}`} />
        <span className="text-xs text-slate-500" data-testid={`cf-faces-${s.id}`}>{s.faces} rosto(s) cadastrado(s)</span>
      </div>
      <div className="grid gap-2 md:grid-cols-[1fr_1fr_auto_auto_auto_auto]">
        <Input placeholder="https://compreface.suaescola.com.br" value={url} onChange={(e) => setUrl(e.target.value)} data-testid={`cf-url-${s.id}`} />
        <div className="relative"><KeyRound className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input type="password" className="pl-8" autoComplete="new-password" value={key} onChange={(e) => setKey(e.target.value)}
            placeholder={s.compreface_api_key_set ? "Chave salva — preencha só para trocar" : "Chave da API (serviço Recognition)"} data-testid={`cf-key-${s.id}`} /></div>
        <Button disabled={!!busy || !url.trim() || (!key.trim() && !s.compreface_api_key_set)} onClick={save} data-testid={`cf-save-${s.id}`}>
          {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" />Salvar</>}</Button>
        <Button variant="outline" disabled={!!busy || !s.configured} onClick={test} data-testid={`cf-test-${s.id}`}>
          {busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><PlugZap className="mr-1 h-4 w-4" />Testar conexão</>}</Button>
        <Button variant="outline" disabled={!!busy || !s.configured || verify?.status === "RUNNING"} onClick={startVerify} data-testid={`cf-verify-${s.id}`}>
          <ShieldCheck className="mr-1 h-4 w-4" />Verificar vínculos</Button>
        <Button variant="outline" disabled={!!busy} onClick={wipe} className="border-rose-200 text-rose-700 hover:bg-rose-50" data-testid={`cf-wipe-${s.id}`}>
          {busy === "wipe" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="mr-1 h-4 w-4" />Apagar rostos</>}</Button>
      </div>
      {verify && <VerifyResult v={verify} />}
      {res && <TestResult res={res} />}
    </div>
  );
};

export default function Integracoes() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/integrations/compreface").then((r) => setData(r.data)).catch((e) => toast.error(err(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="grid min-h-[40vh] place-items-center"><Loader2 className="h-8 w-8 animate-spin text-blue-700" /></div>;
  const ready = data.schools.filter((s) => s.configured).length;
  return (
    <div data-testid="integracoes-page">
      <PageHeader title="Integrações" subtitle="Status dos serviços externos usados pela plataforma" />
      <Card className="mb-4 border-slate-200 p-5" data-testid="face-card">
        <div className="mb-1 flex flex-wrap items-center gap-3">
          <ScanFace className="h-5 w-5 text-blue-700" /><h3 className="font-semibold text-slate-800">CompreFace (reconhecimento facial)</h3>
          <span className="text-xs text-slate-500" data-testid="face-summary">{ready} de {data.schools.length} escola(s) configurada(s) · similaridade mínima 0,98 · diferença mínima de 0,03 para o 2º aluno</span>
        </div>
        <p className="mb-2 text-xs text-slate-500">Cada escola usa o próprio servidor CompreFace. Informe a URL e a chave do serviço de <b>Recognition</b>. A chave fica guardada no servidor e não é exibida. Ao salvar, os alunos com foto guardada são recadastrados automaticamente.</p>
        {data.schools.map((s) => <SchoolConfig key={s.id} s={s} onSaved={load} />)}
      </Card>
      <Card className="flex flex-wrap items-center gap-3 border-slate-200 p-5" data-testid="email-card">
        <Mail className="h-5 w-5 text-blue-700" /><h3 className="font-semibold text-slate-800">E-mail (avisos de chegada e senhas)</h3>
        <Configured ok={data.email} testid="email-configured" />
      </Card>
    </div>
  );
}
